import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    FormControl,
    Stack,
    Text,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Button, Card, FormLabel, Heading, Input } from '@/components/v4';
import createAddress from '@/API/Addresses/createAddress';
import createEightByEightSignUp from '@/API/Service/createEightByEightSignUp';
import {
    breadCrumbStyling,
    descriptionStyling,
    formLabelStyling,
    stackSpacing,
    stackStyling,
} from 'components/forms/new/formStyles';
import EightByEightAddressPicker from './EightByEightAddressPicker';
import { mapFormToAddressPayload, resolveCountryID } from './addressHelpers';

import useGetCurrentCompany from '@/queries/company/useGetCurrentCompany';
export default function EightByEightCreate({ serviceWizard }) {
    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { data: currentCompanyData } = useGetCurrentCompany();
    const companyID = currentCompanyData?.id;
    const companyName = currentCompanyData?.name;

    const [isSaving, setIsSaving] = useState(false);

    // Address selection reported by the picker.
    const [addressSelection, setAddressSelection] = useState({
        effectiveAddress: {},
        existingAddressID: null,
        addressFilled: false,
        isManual: false,
        countries: [],
    });

    // Contact
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');

    const canContinue =
        addressSelection.addressFilled &&
        phone.trim() !== '' &&
        email.trim() !== '';

    const handleBack = () => serviceWizard.setFlow('CreateService');

    const handleContinue = async () => {
        const { effectiveAddress, existingAddressID, isManual, countries } =
            addressSelection;

        setIsSaving(true);
        try {
            // 1. Reuse the existing 8x8 platform address when one was found,
            //    otherwise create a new address. (Platform-address linking is
            //    not done at this sign-up phase.)
            let addressID = existingAddressID;
            if (!addressID) {
                const created = await createAddress(
                    mapFormToAddressPayload(effectiveAddress, {
                        companyID,
                        companyName,
                        countryID: resolveCountryID(
                            effectiveAddress.country,
                            countries,
                        ),
                    }),
                );
                addressID = created?.id ?? null;
            }

            // 2. Sign up the 8x8 service. The created service definition (with
            //    its serviceID + catalogItems) drives the next screen.
            const service = await createEightByEightSignUp({
                companyID,
                addressID,
                phoneNumber: phone,
                emailAddress: email,
            });

            serviceWizard.setEightByEightCompanyDetails({
                addressID,
                address: {
                    '@type': 'GeographicAddressContactMedium',
                    contactType: 'workAddress',
                    preferred: false,
                    ...effectiveAddress,
                    ...(isManual && { isPlatformAddress: true }),
                },
                phone,
                email,
                service,
            });
            serviceWizard.setFlow('LicenceEntitlements');
        } catch (error) {
            toast.error(
                error?.response?.data?.title ??
                    error?.response?.data?.message ??
                    'There was an error setting up the 8x8 service.',
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box maxWidth="1064px" position="relative">
            <Breadcrumb sx={breadCrumbStyling}>
                <BreadcrumbItem>
                    <BreadcrumbLink
                        onClick={() => serviceWizard.setFlow('Start')}>
                        Dashboard
                    </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem>
                    <BreadcrumbLink
                        onClick={() => serviceWizard.setFlow('CreateService')}>
                        Add New Service
                    </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                    <BreadcrumbLink>8×8</BreadcrumbLink>
                </BreadcrumbItem>
            </Breadcrumb>

            <Card
                borderRadius={15}
                p="30px"
                maxWidth="895px"
                ml={0}
                mt={2}
                bg={darkMode ? 'dark.tcap' : null}>
                <Heading
                    as="h3"
                    size="md"
                    mb={6}
                    color={darkMode ? 'white' : 'gray.700'}>
                    Company Details
                </Heading>

                {/* Address */}
                <EightByEightAddressPicker
                    darkMode={darkMode}
                    currentCompany={currentCompany}
                    onChange={setAddressSelection}
                />

                {/* Phone */}
                <FormControl sx={{ margin: '0 !important' }}>
                    <Stack
                        direction="row"
                        spacing={stackSpacing}
                        sx={stackStyling(true)}>
                        <Box>
                            <FormLabel sx={formLabelStyling(darkMode)}>
                                Phone{' '}
                                <span style={{ color: '#e53e3e' }}>*</span>
                            </FormLabel>
                            <Text sx={descriptionStyling} maxWidth="200px">
                                The main company contact number.
                            </Text>
                        </Box>
                        <Box
                            flex="1"
                            minWidth="450px"
                            sx={{ paddingTop: '15px' }}>
                            <Input
                                placeholder="+61390001234"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                type="tel"
                            />
                        </Box>
                    </Stack>
                </FormControl>

                {/* Email */}
                <FormControl sx={{ margin: '0 !important' }}>
                    <Stack
                        direction="row"
                        spacing={stackSpacing}
                        sx={stackStyling(true)}>
                        <Box>
                            <FormLabel sx={formLabelStyling(darkMode)}>
                                Email Address{' '}
                                <span style={{ color: '#e53e3e' }}>*</span>
                            </FormLabel>
                            <Text sx={descriptionStyling} maxWidth="200px">
                                The primary contact email.
                            </Text>
                        </Box>
                        <Box
                            flex="1"
                            minWidth="450px"
                            sx={{ paddingTop: '15px' }}>
                            <Input
                                placeholder="admin@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                type="email"
                            />
                        </Box>
                    </Stack>
                </FormControl>
            </Card>

            <Stack direction="row" spacing={3} mt={5}>
                <Button variant="outline" onClick={handleBack}>
                    Back
                </Button>
                <Button
                    isDisabled={!canContinue || isSaving}
                    isLoading={isSaving}
                    loadingText="Saving"
                    onClick={handleContinue}>
                    Continue
                </Button>
            </Stack>
        </Box>
    );
}
